import React, { createContext, useContext, useState, useMemo } from "react";

const translations = {
  en: {
    nav: {
      services: "Services",
      about: "About",
      book: "Book",
      gallery: "Gallery",
      quote: "Quote",
      contact: "Contact",
      callNow: "Call Now",
    },
    hero: {
      overline: "Mont-Royal · Repairs · Renovations · Maintenance",
      title1: "Repairs that hold.",
      title2: "Maintenance you can count on.",
      titleAccent: "",
      subtitle:
        "Cesar's Repairs is your one-call crew for interior fixes, renovations and the everyday maintenance that keeps a Montreal home running. Sticky door today, finished basement next month — same trusted team.",
      cta1: "Get a free quote",
      cta2: "Call +1 (438) 874-3855",
      badge1: "RBQ 5795-0118-01",
      badge2: "Open 6 days a week",
      badge3: "Mont-Royal based",
    },
    services: {
      overline: "What we handle",
      title: "One crew. Every job.",
      subtitle:
        "From small interior repairs to full kitchen and bath renovations — one trusted crew, finished cleanly. No juggling five different contractors.",
      items: {
        repairs: {
          name: "Interior repairs & maintenance",
          desc: "Doors, drywall, trim, fixtures, leaky faucets, sticking hinges — the steady stream of small interior jobs that keep a home running. We show up, we finish.",
        },
        cleanup: {
          name: "Leaks & electrical fixes",
          desc: "Dripping faucets, slow drains, faulty outlets, light fixtures, switches and the everyday hiccups a homeowner shouldn't have to live with.",
        },
        snow: {
          name: "Painting & drywall",
          desc: "Interior painting, drywall patching, ceiling repairs and crown moulding — finished cleanly so you barely notice we were there.",
        },
        lawn: {
          name: "Carpentry & built-ins",
          desc: "Custom shelving, built-in cabinets, trim work and interior carpentry tailored to your space.",
        },
        renovation: {
          name: "Kitchen & bath renovation",
          desc: "Full or partial kitchen and bathroom remodels, tile work and finishing — managed end-to-end by the same crew who handles your everyday repairs.",
        },
        design: {
          name: "Flooring & tiling",
          desc: "Hardwood, tile, laminate and vinyl — installation, repairs and refinishing across every room of the house.",
        },
      },
    },
    about: {
      overline: "About Cesar",
      title: "A local crew that actually picks up the phone.",
      body1:
        "Cesar's Repairs is a family-run outfit based right here in Mont-Royal. We're the team you call first for interior repairs and maintenance — and the same crew handles renovations, painting, flooring and finishing when it's time to upgrade.",
      body2:
        "No pushy sales, no vague estimates. Just quality work, fair prices and the reliability you only get from a neighbour.",
      stats: [
        { value: "5+", label: "Years serving Montreal" },
        { value: "50+", label: "Properties maintained" },
        { value: "6 days", label: "A week, 7am to 7pm" },
      ],
    },
    booking: {
      overline: "Book a service",
      title: "Lock in your date.",
      subtitle:
        "Pick a service, pick a date, and we'll confirm the visit by phone. Transparent pricing — no surprises.",
      priceListLabel: "Service pricing",
      summaryLabel: "Your selection",
      summaryEmpty: "Pick a service to see your total.",
      totalLabel: "Total",
      totalEmpty: "Select a service and date to continue.",
      services: {
        general_repair: "General Repair Visit",
        home_maintenance: "Home Maintenance (2 hours)",
        lawn_care: "Lawn Care Service",
        yard_cleanup: "Yard Cleanup",
        emergency_repair: "Emergency Repair",
      },
      labels: {
        name: "Full name",
        email: "Email",
        phone: "Phone",
        address: "Service address",
        service: "Service",
        date: "Preferred date",
        time: "Preferred time",
        notes: "Additional notes (optional)",
      },
      placeholders: {
        name: "Jane Tremblay",
        email: "jane@example.com",
        phone: "(438) 555-0199",
        address: "Street, city, postal code",
        service: "Choose a service",
        date: "Pick a date",
        notes: "Gate code, parking, anything we should know...",
      },
      slots: {
        morning: "Morning",
        afternoon: "Afternoon",
        evening: "Evening",
      },
      submit: "Book Now",
      submitting: "Sending...",
      successTitle: "Booking received.",
      successBody: "Cesar will confirm your appointment by phone shortly.",
      errorTitle: "Something went wrong.",
      errorMissing: "Please complete all required fields.",
      errorBody: "Please try again or call us directly.",
    },
    gallery: {
      overline: "Our work",
      title: "Recent jobs around the neighbourhood.",
    },
    quote: {
      overline: "Free quote",
      title: "Tell us about your property.",
      subtitle:
        "Fill out the form and we'll get back to you within one business day. For urgent work, please call directly.",
      labels: {
        name: "Full name",
        email: "Email",
        phone: "Phone",
        address: "Service address",
        service: "Service needed",
        message: "Tell us more (optional)",
      },
      placeholders: {
        name: "Jane Tremblay",
        email: "jane@example.com",
        phone: "(438) 555-0199",
        address: "Street, city, postal code",
        service: "Select a service",
        message: "Size of property, timeline, anything helpful...",
      },
      services: [
        "Interior repairs & maintenance",
        "Kitchen & bath renovation",
        "Painting & drywall",
        "Flooring & tiling",
        "Carpentry & built-ins",
        "Leaks & electrical fixes",
        "Other",
      ],
    },
    hours: {
      overline: "Business hours",
      submit: "Request my quote",
      submitting: "Sending...",
      successTitle: "Thanks — we got it.",
      successBody: "Cesar will reach out within one business day.",
      errorTitle: "Something went wrong.",
      errorBody: "Please try again or call us directly.",
    },
    hours: {
      overline: "Business hours",
      title: "Open 6 days a week.",
      rows: [
        ["Monday – Saturday", "7:00 AM – 7:00 PM"],
        ["Sunday", "Closed"],
      ],
    },
    contact: {
      overline: "Contact",
      title: "Stop by or call anytime.",
      phoneLabel: "Phone",
      addressLabel: "Address",
      phone: "+1 (438) 874-3855",
      address: "2900 Chem. de la Côte-de-Liesse, Mont-Royal, QC H4N 2P2",
    },
    footer: {
      tagline: "Interior repairs, renovations & maintenance",
      rights: "All rights reserved.",
      nav: "Navigate",
      hours: "Hours",
      reach: "Reach us",
    },
  },
  fr: {
    nav: {
      services: "Services",
      about: "À propos",
      book: "Réserver",
      gallery: "Galerie",
      quote: "Soumission",
      contact: "Contact",
      callNow: "Appeler",
    },
    hero: {
      overline: "Mont-Royal · Réparations · Rénovations · Entretien",
      title1: "Réparations qui tiennent.",
      title2: "Entretien fiable.",
      titleAccent: "",
      subtitle:
        "Cesar's Repairs, c'est l'équipe qu'on appelle pour les réparations intérieures, les rénovations et l'entretien quotidien d'une maison montréalaise. Une porte qui coince aujourd'hui, un sous-sol fini le mois prochain — la même équipe de confiance.",
      cta1: "Soumission gratuite",
      cta2: "Appeler +1 (438) 874-3855",
      badge1: "RBQ 5795-0118-01",
      badge2: "Ouvert 6 jours/7",
      badge3: "Basé à Mont-Royal",
    },
    services: {
      overline: "Ce qu'on fait",
      title: "Une équipe. Tous les travaux.",
      subtitle:
        "Des petites réparations intérieures aux rénovations complètes de cuisine et salle de bain — une seule équipe de confiance, du début à la fin.",
      items: {
        repairs: {
          name: "Réparations & entretien intérieurs",
          desc: "Portes, gypse, moulures, robinets qui fuient, charnières qui coincent — les petits travaux intérieurs constants qui gardent une maison en bon état. On vient, on finit.",
        },
        cleanup: {
          name: "Fuites & électricité",
          desc: "Robinets qui coulent, drains lents, prises défectueuses, luminaires, interrupteurs et tous les petits ennuis quotidiens d'un propriétaire.",
        },
        snow: {
          name: "Peinture & gypse",
          desc: "Peinture intérieure, réparation de gypse, plafonds et moulures — un fini propre, comme si on n'était jamais passés.",
        },
        lawn: {
          name: "Menuiserie & intégrés",
          desc: "Étagères sur mesure, armoires intégrées, moulures et menuiserie intérieure adaptées à votre espace.",
        },
        renovation: {
          name: "Rénovation cuisine & salle de bain",
          desc: "Rénovations complètes ou partielles de cuisine et de salle de bain, céramique et finitions — gérées de A à Z par la même équipe que pour vos réparations quotidiennes.",
        },
        design: {
          name: "Plancher & céramique",
          desc: "Bois franc, céramique, laminé et vinyle — installation, réparation et refinition dans chaque pièce de la maison.",
        },
      },
    },
    about: {
      overline: "À propos de Cesar",
      title: "Une équipe locale qui répond vraiment au téléphone.",
      body1:
        "Cesar's Repairs est une entreprise familiale basée à Mont-Royal. C'est l'équipe qu'on appelle d'abord pour les réparations et l'entretien intérieurs — et la même équipe s'occupe des rénovations, de la peinture, des planchers et des finitions quand vient le temps de mettre à jour.",
      body2:
        "Pas de vente sous pression, pas d'estimés vagues. Juste du bon travail, des prix équitables et la fiabilité d'un voisin.",
      stats: [
        { value: "5+", label: "Années à Montréal" },
        { value: "50+", label: "Propriétés entretenues" },
        { value: "6 jours", label: "Sur 7, 7h à 19h" },
      ],
    },
    booking: {
      overline: "Réserver un service",
      title: "Choisissez votre date.",
      subtitle:
        "Choisissez un service, une date, et on confirme la visite par téléphone. Tarification transparente — sans surprise.",
      priceListLabel: "Tarifs des services",
      summaryLabel: "Votre choix",
      summaryEmpty: "Choisissez un service pour voir le total.",
      totalLabel: "Total",
      totalEmpty: "Sélectionnez un service et une date pour continuer.",
      services: {
        general_repair: "Visite de réparation générale",
        home_maintenance: "Entretien résidentiel (2 heures)",
        lawn_care: "Entretien de pelouse",
        yard_cleanup: "Nettoyage de cour",
        emergency_repair: "Réparation d'urgence",
      },
      labels: {
        name: "Nom complet",
        email: "Courriel",
        phone: "Téléphone",
        address: "Adresse du service",
        service: "Service",
        date: "Date souhaitée",
        time: "Plage horaire",
        notes: "Notes supplémentaires (facultatif)",
      },
      placeholders: {
        name: "Jean Tremblay",
        email: "jean@exemple.com",
        phone: "(438) 555-0199",
        address: "Rue, ville, code postal",
        service: "Choisir un service",
        date: "Choisir une date",
        notes: "Code de barrière, stationnement, info utile...",
      },
      slots: {
        morning: "Matin",
        afternoon: "Après-midi",
        evening: "Soir",
      },
      submit: "Réserver",
      submitting: "Envoi...",
      successTitle: "Réservation reçue.",
      successBody: "Cesar confirmera votre rendez-vous par téléphone bientôt.",
      errorTitle: "Une erreur est survenue.",
      errorMissing: "Veuillez remplir tous les champs requis.",
      errorBody: "Veuillez réessayer ou appeler directement.",
    },
    gallery: {
      overline: "Nos réalisations",
      title: "Travaux récents dans le quartier.",
    },
    quote: {
      overline: "Soumission gratuite",
      title: "Parlez-nous de votre propriété.",
      subtitle:
        "Remplissez le formulaire et nous vous répondrons en moins d'un jour ouvrable. Pour les urgences, appelez-nous directement.",
      labels: {
        name: "Nom complet",
        email: "Courriel",
        phone: "Téléphone",
        address: "Adresse du service",
        service: "Service requis",
        message: "Détails (facultatif)",
      },
      placeholders: {
        name: "Jean Tremblay",
        email: "jean@exemple.com",
        phone: "(438) 555-0199",
        address: "Rue, ville, code postal",
        service: "Choisir un service",
        message: "Taille du terrain, échéance, informations utiles...",
      },
      services: [
        "Réparations & entretien intérieurs",
        "Rénovation cuisine & salle de bain",
        "Peinture & gypse",
        "Plancher & céramique",
        "Menuiserie & intégrés",
        "Fuites & électricité",
        "Autre",
      ],
      submit: "Demander ma soumission",
      submitting: "Envoi...",
      successTitle: "Merci — c'est reçu.",
      successBody: "Cesar vous contactera en moins d'un jour ouvrable.",
      errorTitle: "Une erreur est survenue.",
      errorBody: "Veuillez réessayer ou appeler directement.",
    },
    hours: {
      overline: "Heures d'ouverture",
      title: "Ouvert 6 jours sur 7.",
      rows: [
        ["Lundi – Samedi", "7h00 – 19h00"],
        ["Dimanche", "Fermé"],
      ],
    },
    contact: {
      overline: "Contact",
      title: "Passez ou appelez quand vous voulez.",
      phoneLabel: "Téléphone",
      addressLabel: "Adresse",
      phone: "+1 (438) 874-3855",
      address: "2900 Chem. de la Côte-de-Liesse, Mont-Royal, QC H4N 2P2",
    },
    footer: {
      tagline: "Réparations, rénovations & entretien intérieurs",
      rights: "Tous droits réservés.",
      nav: "Navigation",
      hours: "Horaire",
      reach: "Nous joindre",
    },
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState("fr");
  const value = useMemo(
    () => ({
      lang,
      setLang,
      toggle: () => setLang((l) => (l === "en" ? "fr" : "en")),
      t: translations[lang],
    }),
    [lang]
  );
  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
